// ============================================================================
// BookingOverviewWidget — Phase 2
// ============================================================================
// Renders the 6 booking KPI tiles + a status breakdown donut chart.
// Data: vew_booking_list (counts) + vew_occupancy_summary (occupancy %).

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import ReactECharts from 'echarts-for-react';
import KpiTile from './KpiTile';
import WidgetShell from './WidgetShell';
import { useBookingOverviewWidget } from 'hooks/api/useDashboard';
import type { DashboardFilter } from 'api/types';
import { formatCurrencyShort, formatNumberShort, formatPercent } from 'utils/currencyFormat';

interface Props {
  filter: DashboardFilter;
  enabled: boolean;
}

const STATUS_COLORS = {
  CONFIRMED: '#16a34a',
  PENDING:   '#f59e0b',
  CANCELLED: '#dc2626',
  OTHER:     '#94a3b8',
};

const BookingOverviewWidget: React.FC<Props> = ({ filter, enabled }) => {
  const { data, loading, error } = useBookingOverviewWidget(filter, enabled);

  const chartOption = useMemo(() => {
    if (!data) return null;
    const others = Math.max(
      0,
      data.totalBookings
        - data.confirmedBookings
        - data.pendingApprovalBookings
        - data.cancelledBookings,
    );
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend:  { bottom: 0, left: 'center', textStyle: { fontSize: 11 } },
      series: [{
        name: 'Bookings',
        type: 'pie',
        radius: ['45%', '70%'],
        avoidLabelOverlap: true,
        label: { show: false },
        labelLine: { show: false },
        data: [
          { value: data.confirmedBookings,       name: 'Confirmed',  itemStyle: { color: STATUS_COLORS.CONFIRMED } },
          { value: data.pendingApprovalBookings, name: 'Pending',    itemStyle: { color: STATUS_COLORS.PENDING   } },
          { value: data.cancelledBookings,       name: 'Cancelled',  itemStyle: { color: STATUS_COLORS.CANCELLED } },
          { value: others,                       name: 'Other',      itemStyle: { color: STATUS_COLORS.OTHER     } },
        ].filter(s => s.value > 0),
      }],
    };
  }, [data]);

  return (
    <WidgetShell
      title="Booking Overview"
      icon={<IconSystem.file size={16} />}
      loading={loading || !enabled}
      error={error}
      empty={!!data && data.totalBookings === 0}
      emptyText="No bookings in this window."
    >
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div className="kpi-strip">
            <KpiTile label="Total Bookings"  value={formatNumberShort(data.totalBookings)} />
            <KpiTile label="Confirmed"       value={formatNumberShort(data.confirmedBookings)} />
            <KpiTile label="Pending Approval" value={formatNumberShort(data.pendingApprovalBookings)} />
            <KpiTile label="Cancelled"       value={formatNumberShort(data.cancelledBookings)} />
            <KpiTile label="Complimentary"   value={formatNumberShort(data.complimentaryBookings)} />
            <KpiTile label="Discounted"      value={formatNumberShort(data.discountedBookings)} />
            <KpiTile label="Occupancy"       value={formatPercent(data.occupancyPercent)} />
            <KpiTile label="ADR"             value={formatCurrencyShort(data.averageDailyRate)} />
          </div>

          {chartOption && (
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.25rem' }}>
                Status breakdown
              </div>
              <ReactECharts
                option={chartOption}
                style={{ height: 220, width: '100%' }}
                opts={{ renderer: 'svg' }}
                notMerge
              />
            </div>
          )}
        </div>
      )}
    </WidgetShell>
  );
};

export default BookingOverviewWidget;
